import React, { useState } from 'react';
import { Header } from '../../components/Header';
import { Footer } from '../../components/Footer';
import { LocaleProvider, useLocale } from '../../i18n/LocaleContext';
import { useSeo } from '../../seo/useSeo';
import { breadcrumbJsonLd } from '../../seo/breadcrumbs';
import { RELEASES, Release } from './releases';
import '../../styles/landing.css';
import '../../styles/landing-neon.css';

const PAGE_SIZE = 5;

const ReleaseCard = ({ r }: { r: Release }): React.JSX.Element => {
  const { t } = useLocale();
  return (
  <article className="release-card">
    <header className="release-head">
      {r.version && <span className="release-version">v{r.version}</span>}
      <span className="release-date">{r.date}</span>
      {r.tag && <span className="release-tag">{r.tag}</span>}
    </header>
    <h2 className="release-title">{r.title}</h2>
    <p className="release-desc">{r.description}</p>

    {r.highlights && r.highlights.length > 0 && (
      <div className="release-block">
        <h3>{t('news.new')}</h3>
        <ul>
          {r.highlights.map((h, i) => (
            <li key={i}>{h}</li>
          ))}
        </ul>
      </div>
    )}

    {r.fixes && r.fixes.length > 0 && (
      <div className="release-block">
        <h3>{t('news.fixes')}</h3>
        <ul>
          {r.fixes.map((f, i) => (
            <li key={i}>{f}</li>
          ))}
        </ul>
      </div>
    )}
  </article>
  );
};

const WhatsNewInner = (): React.JSX.Element => {
  const { t } = useLocale();
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(false);

  const shown = RELEASES.slice(0, visible);
  const hasMore = visible < RELEASES.length;

  useSeo({
    title: "What's New",
    description:
      "Latest releases, features, and fixes shipped in Gather. See what's new in the social events app for clubs, meetups, and friends nearby.",
    canonical: '/whats-new',
    jsonLd: breadcrumbJsonLd([
      { name: 'Home', path: '/' },
      { name: "What's new", path: '/whats-new' },
    ]),
  });

  const loadMore = (): void => {
    if (loading) return;
    setLoading(true);
    setTimeout(() => {
      setVisible((v) => Math.min(v + PAGE_SIZE, RELEASES.length));
      setLoading(false);
    }, 350);
  };

  return (
    <div className="landing landing--neon" id="top">
      <Header />
      <main>
        <section className="whats-new-page">
          <div className="wrap" style={{ maxWidth: 760 }}>
            <header className="whats-new-hero">
              <span className="eyebrow">{t('news.eyebrow')}</span>
              <h1>{t('news.h')}</h1>
              <p className="whats-new-intro">{t('news.intro')}</p>
            </header>

            <div className="release-list">
              {shown.map((r, i) => (
                <ReleaseCard r={r} key={`${r.date}-${i}`} />
              ))}
            </div>

            {hasMore && (
              <div className="release-load">
                <button
                  type="button"
                  className="btn btn-outline on-dark"
                  onClick={loadMore}
                  disabled={loading}
                >
                  {loading ? t('news.loading') : t('news.loadMore')}
                </button>
              </div>
            )}

            {!hasMore && shown.length > 0 && (
              <p className="release-end">{t('news.end')}</p>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export const WhatsNew = (): React.JSX.Element => (
  <LocaleProvider>
    <WhatsNewInner />
  </LocaleProvider>
);
